/**
 * Request Manager - Prevents duplicate API requests
 * Tracks ongoing requests to avoid multiple simultaneous calls to the same endpoint
 */
class RequestManager {
  constructor () {
    this.pendingRequests = new Map()
  }

  /**
   * Execute a request only if not already pending
   * @param {string} key - Unique key for the request
   * @param {Function} requestFn - Function that returns a Promise
   * @returns {Promise} - The request promise
   */
  async execute (key, requestFn) {
    // If the request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)
    }

    // Check if tab is active before making request
    if (document.hidden) {
      // Tab is inactive, return a resolved promise to avoid connection errors
      return Promise.resolve()
    }

    // Create a new request
    const requestPromise = requestFn()
      .catch((error) => {
        // Handle connection errors gracefully (browser may close connections when tab becomes inactive)
        const isConnectionError = 
          error.code === 'ERR_CONNECTION_CLOSED' ||
          error.message?.includes('ERR_CONNECTION_CLOSED') ||
          error.message?.includes('Network Error') ||
          error.message?.includes('Connection closed') ||
          (error.request && !error.response && document.hidden) // Request made but tab became inactive
        
        if (isConnectionError) {
          // Silently handle connection closed errors (likely due to tab switching)
          // Don't log or throw - just resolve silently
          return Promise.resolve()
        }
        // Re-throw other errors
        throw error
      })
      .finally(() => {
        // Clean up when a request completes
        this.pendingRequests.delete(key)
      })

    // Store the promise
    this.pendingRequests.set(key, requestPromise)
    
    return requestPromise
  }

  /**
   * Check if a request is currently pending
   * @param {string} key - Request key
   * @returns {boolean}
   */
  isPending (key) {
    return this.pendingRequests.has(key)
  }

  /**
   * Cancel a pending request
   * @param {string} key - Request key
   */
  cancel (key) {
    if (this.pendingRequests.has(key)) {
      this.pendingRequests.delete(key)
    }
  }

  /**
   * Clear all pending requests
   */
  clear () {
    this.pendingRequests.clear()
  }

  /**
   * Cancel all pending requests (useful when tab becomes inactive)
   */
  cancelAll () {
    this.pendingRequests.clear()
  }
}

// Export singleton instance
export default new RequestManager()
